import { createLogger } from "../utils/logger.js";
import { recordWriteFailure } from "./recording-slice.js";
//#region ../src/state/persistence-middleware.ts
const log = createLogger("PersistenceMiddleware");
/** Maximum number of concurrent OPFS write operations. */
const MAX_CONCURRENT_WRITES = 3;
/**
* Simple write queue that limits concurrent OPFS operations.
* Prevents unbounded memory growth when storage is slow.
*/
var WriteQueue = class {
	pendingCount = 0;
	queue = [];
	enqueue(writeFn) {
		this.queue.push(writeFn);
		this.drain();
	}
	drain() {
		while (this.pendingCount < MAX_CONCURRENT_WRITES && this.queue.length > 0) {
			const fn = this.queue.shift();
			this.pendingCount++;
			fn().finally(() => {
				this.pendingCount--;
				this.drain();
			});
		}
	}
};
/**
* Create a Redux middleware that persists qualifying actions to storage
* during active recording sessions.
*
* Persistence rules:
* - Only persists when `state.recording.isRecording` is true (checked AFTER
*   the action is reduced, so `startSession` itself is included).
* - Persists `gpsData/*` and `recording/*` actions.
* - Excludes `recording/recordWriteFailure` to prevent recursive persistence.
* - Excludes `routing/*`, `refPoints/*`, and any other non-recording actions.
* - Uses 1-based indexing for action files (000001.json, 000002.json, …).
* - Each middleware instance maintains its own action index (Bug 10 fix).
*/
function createPersistenceMiddleware(options) {
	const { storageBackend, onWriteFailure } = options;
	let actionIndex = 0;
	const writeQueue = new WriteQueue();
	const middleware = (store) => (next) => (action) => {
		const typedAction = action;
		const actionType = typeof typedAction.type === "string" ? typedAction.type : null;
		const wasRecording = store.getState().recording?.isRecording ?? false;
		const result = next(action);
		if (!actionType) return result;
		if (actionType === "recording/startSession") actionIndex = 0;
		if (!((store.getState().recording?.isRecording ?? false) || wasRecording && actionType === "recording/endSession")) return result;
		if (!(actionType.startsWith("gpsData/") || actionType.startsWith("recording/") && actionType !== "recording/recordWriteFailure")) return result;
		const index = ++actionIndex;
		writeQueue.enqueue(async () => {
			try {
				await storageBackend.writeAction(typedAction, index);
			} catch (err) {
				const normalized = err instanceof Error ? err : new Error(String(err));
				log.error("Failed to persist action:", normalized);
				store.dispatch(recordWriteFailure(normalized.message));
				if (onWriteFailure) onWriteFailure(normalized);
			}
		});
		return result;
	};
	return middleware;
}
//#endregion
export { createPersistenceMiddleware };
